import React, { useState, useEffect, useMemo, useRef } from 'react';
import Layout from '../components/Layout';
import { api } from '../App';
import { Plus, ArrowUpRight, ArrowDownRight, ArrowUpDown, ChevronDown, ChevronUp, Shield, ShieldOff, Calendar, User, Hash, Banknote, FileText, Building2, Filter, X, Wallet, Search, Upload, Pencil, Trash2, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { EditTransactionDialog, DeleteTransactionDialog } from '../components/transactions/TransactionEditDelete';

const Transactions = ({ user, setUser }) => {
  const [transactions, setTransactions] = useState([]);
  const [members, setMembers] = useState([]);
  const [lasAccounts, setLASAccounts] = useState([]);
  const [mtfAccounts, setMTFAccounts] = useState([]);
  const [dematAccounts, setDematAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [expandedId, setExpandedId] = useState(null);
  const [editTxn, setEditTxn] = useState(null);
  const [deleteTxn, setDeleteTxn] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    member_id: '',
    symbol: '',
    transaction_type: '',
    dateFrom: '',
    dateTo: '',
  });
  const [formData, setFormData] = useState({
    member_id: '',
    transaction_type: 'buy_primary',
    symbol: '',
    quantity: 0,
    price: 0,
    transaction_date: new Date().toISOString().split('T')[0],
    purchase_type: 'Primary',
    mtf_account_id: '',
    demat_account_id: '',
  });

  // Symbol autocomplete state
  const [symbolQuery, setSymbolQuery] = useState('');
  const [symbolSuggestions, setSymbolSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingSymbols, setSearchingSymbols] = useState(false);
  const symbolInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        symbolInputRef.current && 
        !symbolInputRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced symbol search
  useEffect(() => {
    const searchSymbols = async () => {
      if (!symbolQuery || symbolQuery.length < 1) {
        setSymbolSuggestions([]);
        return;
      }

      setSearchingSymbols(true);
      try {
        const res = await api.get(`/symbols/search?q=${encodeURIComponent(symbolQuery)}`);
        setSymbolSuggestions(res.data || []);
        setShowSuggestions(true);
      } catch (err) {
        console.error('Symbol search failed:', err);
        setSymbolSuggestions([]);
      } finally {
        setSearchingSymbols(false);
      }
    };

    const debounceTimer = setTimeout(searchSymbols, 300);
    return () => clearTimeout(debounceTimer);
  }, [symbolQuery]);

  const fetchData = async () => {
    try {
      const [transactionsRes, membersRes, lasRes, mtfRes, dematRes] = await Promise.all([
        api.get('/transactions'),
        api.get('/family-members'),
        api.get('/las-accounts'),
        api.get('/mtf-accounts'),
        api.get('/demat-accounts')
      ]);
      setTransactions(transactionsRes.data);
      setMembers(membersRes.data);
      setLASAccounts(lasRes.data);
      setMTFAccounts(mtfRes.data);
      setDematAccounts(dematRes.data);
    } catch (err) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  // Get demat accounts for a specific member
  const getDematAccountsForMember = (memberId) => {
    return dematAccounts.filter(a => a.member_id === memberId);
  };

  // Get unique symbols from transactions for filter dropdown
  const uniqueSymbols = useMemo(() => {
    return [...new Set(transactions.map(t => t.symbol))].sort();
  }, [transactions]);

  // Get unique transaction types for filter dropdown
  const transactionTypes = [
    { value: 'buy', label: 'Buy' },
    { value: 'sell', label: 'Sell' },
    { value: 'las_pledge', label: 'LAS Pledge' },
    { value: 'las_unpledge', label: 'LAS Unpledge' },
  ];

  // Check if any filters are active
  const hasActiveFilters = filters.member_id || filters.symbol || filters.transaction_type || filters.dateFrom || filters.dateTo;

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      member_id: '',
      symbol: '',
      transaction_type: '',
      dateFrom: '',
      dateTo: '',
    });
  };

  // Filter and sort transactions
  const getFilteredAndSortedTransactions = useMemo(() => {
    let allTxns = [...transactions];
    
    // Apply filters
    if (filters.member_id) {
      allTxns = allTxns.filter(t => t.member_id === filters.member_id);
    }
    if (filters.symbol) {
      allTxns = allTxns.filter(t => t.symbol === filters.symbol);
    }
    if (filters.transaction_type) {
      allTxns = allTxns.filter(t => t.transaction_type === filters.transaction_type);
    }
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      allTxns = allTxns.filter(t => new Date(t.transaction_date) >= fromDate);
    }
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999); // End of day
      allTxns = allTxns.filter(t => new Date(t.transaction_date) <= toDate);
    }
    
    // Sort based on sortConfig
    allTxns.sort((a, b) => {
      let aVal, bVal;
      
      switch(sortConfig.key) {
        case 'date':
          aVal = new Date(a.transaction_date).getTime();
          bVal = new Date(b.transaction_date).getTime();
          break;
        case 'symbol':
          return sortConfig.direction === 'asc' 
            ? a.symbol.localeCompare(b.symbol) 
            : b.symbol.localeCompare(a.symbol);
        case 'quantity':
          aVal = a.quantity;
          bVal = b.quantity;
          break;
        case 'price':
          aVal = a.price;
          bVal = b.price;
          break;
        case 'total':
          aVal = a.quantity * a.price;
          bVal = b.quantity * b.price;
          break;
        case 'type':
          return sortConfig.direction === 'asc' 
            ? a.transaction_type.localeCompare(b.transaction_type) 
            : b.transaction_type.localeCompare(a.transaction_type);
        case 'member': {
          const memMap = Object.fromEntries((members || []).map(m => [m.id, m.name]));
          const aName = memMap[a.member_id] || '';
          const bName = memMap[b.member_id] || '';
          return sortConfig.direction === 'asc'
            ? aName.localeCompare(bName)
            : bName.localeCompare(aName);
        }
        default:
          aVal = new Date(a.transaction_date).getTime();
          bVal = new Date(b.transaction_date).getTime();
      }
      
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return allTxns;
  }, [transactions, filters, sortConfig, members]);

  // Summary/totals for the currently filtered set
  const filteredSummary = useMemo(() => {
    const txns = getFilteredAndSortedTransactions;
    const s = {
      count: txns.length,
      buyCount: 0, sellCount: 0,
      buyQty: 0, sellQty: 0,
      buyValue: 0, sellValue: 0,
      netQty: 0, netValue: 0,
      symbols: new Set(),
      members: new Set(),
    };
    txns.forEach(t => {
      const val = (t.quantity || 0) * (t.price || 0);
      const type = (t.transaction_type || '').toLowerCase();
      if (type.startsWith('buy')) {
        s.buyCount += 1;
        s.buyQty += (t.quantity || 0);
        s.buyValue += val;
      } else if (type.startsWith('sell')) {
        s.sellCount += 1;
        s.sellQty += (t.quantity || 0);
        s.sellValue += val;
      }
      if (t.symbol) s.symbols.add(t.symbol);
      if (t.member_id) s.members.add(t.member_id);
    });
    s.netQty = s.buyQty - s.sellQty;
    s.netValue = s.buyValue - s.sellValue;
    s.symbolsCount = s.symbols.size;
    s.membersCount = s.members.size;
    const memMap = Object.fromEntries((members || []).map(m => [m.id, m.name]));
    s.membersList = [...s.members].map(id => memMap[id] || 'Unknown').sort();
    return s;
  }, [getFilteredAndSortedTransactions, members]);

  // Export currently displayed transactions to CSV
  const exportToCsv = () => {
    const txns = getFilteredAndSortedTransactions;
    if (!txns.length) {
      toast.error('No transactions to export');
      return;
    }
    const memMap = Object.fromEntries((members || []).map(m => [m.id, m.name]));
    const escape = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ['Date', 'Type', 'Symbol', 'Member', 'Quantity', 'Price', 'Total Value', 'Purchase Type', 'Note'];
    const rows = txns.map(t => [
      new Date(t.transaction_date).toISOString().slice(0, 10),
      getTransactionTypeLabel(t.transaction_type, t.purchase_type),
      t.symbol || '',
      memMap[t.member_id] || '',
      t.quantity || 0,
      t.price || 0,
      (t.quantity || 0) * (t.price || 0),
      t.purchase_type || '',
      (t.note || '').replace(/\r?\n/g, ' '),
    ]);
    const filterDesc = [];
    if (filters.member_id) filterDesc.push(`Member=${memMap[filters.member_id] || filters.member_id}`);
    if (filters.symbol) filterDesc.push(`Symbol=${filters.symbol}`);
    if (filters.transaction_type) filterDesc.push(`Type=${filters.transaction_type}`);
    if (filters.dateFrom) filterDesc.push(`From=${filters.dateFrom}`);
    if (filters.dateTo) filterDesc.push(`To=${filters.dateTo}`);
    const summaryBlock = [
      `# Maa Dabbu — Transactions Export`,
      `# Generated: ${new Date().toISOString()}`,
      `# Filters: ${filterDesc.join(' · ') || 'none'}`,
      `# Rows: ${filteredSummary.count} · Buys: ${filteredSummary.buyCount} · Sells: ${filteredSummary.sellCount}`,
      `# Total Buy Value: ${filteredSummary.buyValue.toFixed(2)} · Total Sell Value: ${filteredSummary.sellValue.toFixed(2)} · Net: ${filteredSummary.netValue.toFixed(2)}`,
      `# Members: ${filteredSummary.membersList.join(', ') || 'none'}`,
      '',
    ];
    const csv = [
      ...summaryBlock,
      header.map(escape).join(','),
      ...rows.map(r => r.map(escape).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `maadabbu-transactions-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${txns.length} transactions`);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const renderSortHeader = (columnKey, label) => (
    <th 
      key={columnKey}
      className="text-left py-3 px-4 font-semibold text-sm uppercase tracking-wider text-gray-600 cursor-pointer hover:bg-gray-100"
      onClick={() => handleSort(columnKey)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortConfig.key === columnKey ? (
          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
        ) : (
          <ArrowUpDown size={12} className="text-gray-400" />
        )}
      </div>
    </th>
  );

  const getTransactionTypeLabel = (type, purchaseType) => {
    if (purchaseType === 'MTF') return 'Buy (MTF)';
    if (type === 'buy_primary' || (type === 'buy' && purchaseType === 'Primary')) return 'Buy Primary';
    if (type === 'buy_secondary' || (type === 'buy' && purchaseType === 'Secondary')) return 'Buy Secondary';
    if (type === 'buy') return 'Buy';
    if (type === 'sell') return 'Sell';
    if (type === 'las_pledge') return 'LAS Pledge';
    if (type === 'las_unpledge') return 'LAS Unpledge';
    return type;
  };

  const getTransactionIcon = (type) => {
    if (type === 'las_pledge') return <Shield size={14} />;
    if (type === 'las_unpledge') return <ShieldOff size={14} />;
    if (type === 'sell') return <ArrowUpRight size={14} />;
    return <ArrowDownRight size={14} />;
  };

  const getTransactionBadge = (type, purchaseType) => {
    const label = getTransactionTypeLabel(type, purchaseType);
    const icon = getTransactionIcon(type);
    
    if (type === 'sell') {
      return (
        <span className="badge badge-danger flex items-center gap-1 w-fit">
          {icon}
          {label}
        </span>
      );
    }
    if (type === 'las_pledge' || type === 'las_unpledge') {
      return (
        <span className="badge badge-warning flex items-center gap-1 w-fit">
          {icon}
          {label}
        </span>
      );
    }
    return (
      <span className="badge badge-success flex items-center gap-1 w-fit">
        {icon}
        {label}
      </span>
    );
  };

  const getMemberName = (memberId) => {
    const member = members.find(m => m.id === memberId);
    return member ? member.name : 'Unknown';
  };

  const getMTFAccountName = (mtfAccountId) => {
    const account = mtfAccounts.find(a => a.id === mtfAccountId);
    if (!account) return 'Unknown';
    const member = members.find(m => m.id === account.member_id);
    return `${account.bank_name} - ${member?.name || 'Unknown'}`;
  };

  const getLASAccountInfo = (lasAccountId) => {
    if (!lasAccountId) return null;
    const account = lasAccounts.find(a => a.id === lasAccountId);
    if (!account) return null;
    const member = members.find(m => m.id === account.member_id);
    return {
      bankName: account.bank_name,
      accountNumber: account.account_number,
      memberName: member?.name || 'Unknown'
    };
  };

  const getTransactionDetails = (txn) => {
    const details = [];
    
    // Common details
    details.push({ label: 'Transaction ID', value: txn.id, icon: Hash });
    details.push({ label: 'Date', value: new Date(txn.transaction_date).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }), icon: Calendar });
    details.push({ label: 'Member', value: getMemberName(txn.member_id), icon: User });
    details.push({ label: 'Symbol', value: txn.symbol, icon: FileText });
    details.push({ label: 'Quantity', value: txn.quantity.toLocaleString() + ' shares', icon: Hash });
    details.push({ label: 'Price per Share', value: '₹' + txn.price.toFixed(2), icon: Banknote });
    details.push({ label: 'Total Value', value: '₹' + (txn.quantity * txn.price).toLocaleString('en-IN', { maximumFractionDigits: 2 }), icon: Banknote });
    
    // Type-specific details for pledge/unpledge - always show LAS info
    if (txn.transaction_type === 'las_pledge' || txn.transaction_type === 'las_unpledge') {
      const lasInfo = getLASAccountInfo(txn.las_account_id);
      if (lasInfo) {
        details.push({ label: 'LAS Bank', value: lasInfo.bankName, icon: Building2 });
        details.push({ label: 'LAS Account Holder', value: lasInfo.memberName, icon: User });
        if (lasInfo.accountNumber) {
          details.push({ label: 'LAS Account Number', value: lasInfo.accountNumber, icon: Hash });
        }
      } else if (txn.las_account_id) {
        details.push({ label: 'LAS Account ID', value: txn.las_account_id, icon: Shield });
      }
      if (txn.notes) {
        details.push({ label: 'Notes', value: txn.notes, icon: FileText });
      }
    }
    
    if (txn.purchase_type) {
      details.push({ label: 'Purchase Type', value: txn.purchase_type, icon: FileText });
    }
    
    if (txn.created_at) {
      details.push({ label: 'Recorded On', value: new Date(txn.created_at).toLocaleString('en-IN'), icon: Calendar });
    }
    
    return details;
  };

  // Handle symbol selection from autocomplete
  const handleSymbolSelect = (suggestion) => {
    setFormData({ ...formData, symbol: suggestion.symbol });
    setSymbolQuery(suggestion.symbol);
    setShowSuggestions(false);
  };

  // Handle symbol input change
  const handleSymbolInputChange = (e) => {
    const value = e.target.value.toUpperCase();
    setSymbolQuery(value);
    setFormData({ ...formData, symbol: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Map new transaction types to backend format
    let txnType = formData.transaction_type;
    let purchaseType = 'Primary';
    
    if (txnType === 'buy_primary') {
      txnType = 'buy';
      purchaseType = 'Primary';
    } else if (txnType === 'buy_secondary') {
      txnType = 'buy';
      purchaseType = 'Secondary';
    } else if (txnType === 'buy_mtf') {
      txnType = 'buy';
      purchaseType = 'MTF';
    }
    
    try {
      const payload = {
        ...formData,
        transaction_type: txnType,
        purchase_type: purchaseType,
        transaction_date: new Date(formData.transaction_date).toISOString(),
      };
      
      // Only include mtf_account_id for MTF purchases
      if (purchaseType !== 'MTF') {
        delete payload.mtf_account_id;
      }
      
      // Remove demat_account_id if empty
      if (!payload.demat_account_id) {
        delete payload.demat_account_id;
      }
      
      await api.post('/transactions', payload);
      toast.success(purchaseType === 'MTF' 
        ? 'MTF Transaction recorded and holding added to portfolio' 
        : 'Transaction recorded');
      setOpen(false);
      setFormData({
        member_id: '',
        transaction_type: 'buy_primary',
        symbol: '',
        quantity: 0,
        price: 0,
        transaction_date: new Date().toISOString().split('T')[0],
        purchase_type: 'Primary',
        mtf_account_id: '',
        demat_account_id: '',
      });
      setSymbolQuery('');
      setSymbolSuggestions([]);
      fetchData();
    } catch (err) {
      toast.error('Failed to record transaction');
    }
  };

  const getMTFAccountsForMember = (memberId) => {
    return mtfAccounts.filter(a => a.member_id === memberId);
  };

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="text-center py-20">Loading...</div>
      </Layout>
    );
  }

  const displayTransactions = getFilteredAndSortedTransactions;

  return (
    <Layout user={user} setUser={setUser}>
      <div data-testid="transactions-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-1 sm:mb-2">Transactions</h1>
            <p className="text-sm sm:text-base text-gray-600">Record and view all buy/sell transactions</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            onClick={() => window.location.href = '/bulk-import'}
            className="flex items-center justify-center gap-2 w-full sm:w-auto"
            data-testid="bulk-import-link"
          >
            <Upload size={16} /> Bulk Import
          </Button>
          <Dialog open={open} onOpenChange={(isOpen) => {
            setOpen(isOpen);
            if (!isOpen) {
              setSymbolQuery('');
              setSymbolSuggestions([]);
              setShowSuggestions(false);
            }
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-transaction-button" className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto">
                <Plus size={18} />
                Add Transaction
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Record Transaction</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Member *</label>
                  <select
                    data-testid="transaction-member-select"
                    className="input-field w-full"
                    value={formData.member_id}
                    onChange={(e) => setFormData({ ...formData, member_id: e.target.value })}
                    required
                  >
                    <option value="">Select Member</option>
                    {members.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} ({m.relation})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Type *</label>
                  <select
                    data-testid="transaction-type-select"
                    className="input-field w-full"
                    value={formData.transaction_type}
                    onChange={(e) => setFormData({ ...formData, transaction_type: e.target.value, mtf_account_id: '' })}
                  >
                    <option value="buy_primary">Buy Primary (Direct Purchase)</option>
                    <option value="buy_secondary">Buy Secondary (Secondary Market)</option>
                    <option value="buy_mtf">Buy (MTF)</option>
                    <option value="sell">Sell</option>
                    <option value="las_pledge">LAS Transfer - Pledge</option>
                    <option value="las_unpledge">LAS Transfer - Unpledge</option>
                  </select>
                </div>
                {formData.transaction_type === 'buy_mtf' && (
                  <div>
                    <label className="block text-sm font-medium mb-2">MTF Account *</label>
                    <select
                      data-testid="transaction-mtf-account-select"
                      className="input-field w-full"
                      value={formData.mtf_account_id}
                      onChange={(e) => setFormData({ ...formData, mtf_account_id: e.target.value })}
                      required
                    >
                      <option value="">Select MTF Account</option>
                      {mtfAccounts.map((a) => {
                        const member = members.find(m => m.id === a.member_id);
                        return (
                          <option key={a.id} value={a.id}>
                            {a.bank_name} - {member?.name || 'Unknown'} ({a.account_number})
                          </option>
                        );
                      })}
                    </select>
                    {mtfAccounts.length === 0 && (
                      <p className="text-xs text-amber-600 mt-1">
                        No MTF accounts found. Please create an MTF account first in the MTF Management section.
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      This transaction will also add the holding to the selected member&apos;s portfolio.
                    </p>
                  </div>
                )}
                
                {/* Symbol input with autocomplete */}
                <div className="relative">
                  <label className="block text-sm font-medium mb-2">Symbol *</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      ref={symbolInputRef}
                      type="text"
                      data-testid="transaction-symbol-input"
                      className="input-field w-full pl-9"
                      placeholder="Start typing... e.g., REL, HDFC, TCS"
                      value={symbolQuery || formData.symbol}
                      onChange={handleSymbolInputChange}
                      onFocus={() => symbolQuery && setShowSuggestions(true)}
                      autoComplete="off"
                      required
                    />
                    {searchingSymbols && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full"></div>
                      </div>
                    )}
                  </div>
                  
                  {/* Autocomplete suggestions dropdown */}
                  {showSuggestions && symbolSuggestions.length > 0 && (
                    <div 
                      ref={suggestionsRef}
                      className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                    >
                      {symbolSuggestions.map((suggestion, index) => (
                        <div
                          key={`${suggestion.symbol}-${index}`}
                          className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          onClick={() => handleSymbolSelect(suggestion)}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="font-mono font-semibold text-gray-800">{suggestion.symbol}</span>
                              {suggestion.display_name !== suggestion.symbol && (
                                <span className="text-gray-500 text-sm ml-2">{suggestion.display_name}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {suggestion.source === 'portfolio' && (
                                <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">In Portfolio</span>
                              )}
                              {suggestion.quantity > 0 && (
                                <span className="text-xs text-gray-400">{suggestion.quantity.toLocaleString()} qty</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-500 mt-1">
                    Type to search from your portfolio or known symbols
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Quantity *</label>
                    <input
                      type="number"
                      data-testid="transaction-quantity-input"
                      className="input-field w-full"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Price *</label>
                    <input
                      type="number"
                      data-testid="transaction-price-input"
                      step="0.01"
                      className="input-field w-full"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Date *</label>
                  <input
                    type="date"
                    data-testid="transaction-date-input"
                    className="input-field w-full"
                    value={formData.transaction_date}
                    onChange={(e) => setFormData({ ...formData, transaction_date: e.target.value })}
                    required
                  />
                </div>
                
                {/* Demat Account selector (shown for all buy transactions if member selected) */}
                {formData.member_id && ['buy_primary', 'buy_secondary'].includes(formData.transaction_type) && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Demat Account (Optional)</label>
                    <select
                      data-testid="transaction-demat-account-select"
                      className="input-field w-full"
                      value={formData.demat_account_id}
                      onChange={(e) => setFormData({ ...formData, demat_account_id: e.target.value })}
                    >
                      <option value="">Select Demat Account</option>
                      {getDematAccountsForMember(formData.member_id).map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.bank_name} ({a.depository})
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Optional: Assign this transaction to a specific demat account
                    </p>
                  </div>
                )}
                
                <Button type="submit" className="btn-primary w-full">
                  Record Transaction
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 md:mb-8">
          <div className="card p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-2 sm:p-3 bg-blue-100 rounded-lg flex-shrink-0">
                <FileText className="text-blue-600" size={18} />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-gray-500 truncate">Total Transactions</p>
                <p className="text-xl sm:text-2xl font-bold">{transactions.length}</p>
              </div>
            </div>
          </div>
          <div className="card p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-2 sm:p-3 bg-green-100 rounded-lg flex-shrink-0">
                <ArrowDownRight className="text-green-600" size={18} />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-gray-500 truncate">Total Buys</p>
                <p className="text-xl sm:text-2xl font-bold">{transactions.filter(t => t.transaction_type === 'buy').length}</p>
              </div>
            </div>
          </div>
          <div className="card p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-2 sm:p-3 bg-red-100 rounded-lg flex-shrink-0">
                <ArrowUpRight className="text-red-600" size={18} />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-gray-500 truncate">Total Sells</p>
                <p className="text-xl sm:text-2xl font-bold">{transactions.filter(t => t.transaction_type === 'sell').length}</p>
              </div>
            </div>
          </div>
          <div className="card p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-2 sm:p-3 bg-amber-100 rounded-lg flex-shrink-0">
                <Shield className="text-amber-600" size={18} />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-gray-500 truncate">Pledge/Unpledge</p>
                <p className="text-xl sm:text-2xl font-bold">{transactions.filter(t => ['las_pledge', 'las_unpledge'].includes(t.transaction_type)).length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Panel */}
        <div className="card mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-3">
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant="outline"
                size="sm"
                className={`flex items-center gap-2 ${hasActiveFilters ? 'border-primary text-primary' : ''}`}
              >
                <Filter size={16} />
                Filters {hasActiveFilters && '•'}
              </Button>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="text-sm text-primary hover:underline flex items-center gap-1">
                  <X size={14} />
                  Clear All
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="text-sm text-gray-600">
                Showing {displayTransactions.length} of {transactions.length} transactions
              </div>
              <Button
                onClick={exportToCsv}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                data-testid="export-csv-btn"
                disabled={!displayTransactions.length}
              >
                <Download size={14} />
                Export CSV
              </Button>
            </div>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Member</label>
                <select
                  className="input-field w-full text-sm"
                  value={filters.member_id}
                  onChange={(e) => setFilters({ ...filters, member_id: e.target.value })}
                >
                  <option value="">All Members</option>
                  {members.map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Symbol</label>
                <select
                  className="input-field w-full text-sm"
                  value={filters.symbol}
                  onChange={(e) => setFilters({ ...filters, symbol: e.target.value })}
                >
                  <option value="">All Symbols</option>
                  {uniqueSymbols.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Type</label>
                <select
                  className="input-field w-full text-sm"
                  value={filters.transaction_type}
                  onChange={(e) => setFilters({ ...filters, transaction_type: e.target.value })}
                >
                  <option value="">All Types</option>
                  {transactionTypes.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">From Date</label>
                <input
                  type="date"
                  className="input-field w-full text-sm"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">To Date</label>
                <input
                  type="date"
                  className="input-field w-full text-sm"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                />
              </div>
            </div>
          )}
        </div>

        {/* Filtered summary — always visible when at least one filter is applied OR any rows exist */}
        {displayTransactions.length > 0 && (
          <div className="card p-4 mt-4" data-testid="filtered-summary">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">
                {hasActiveFilters ? 'Summary of filtered transactions' : 'Summary of all transactions'}
              </h3>
              <span className="text-xs text-gray-500">
                {filteredSummary.count} transaction{filteredSummary.count !== 1 ? 's' : ''}
                {filteredSummary.symbolsCount > 0 && ` · ${filteredSummary.symbolsCount} symbol${filteredSummary.symbolsCount !== 1 ? 's' : ''}`}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
              <div className="p-2 rounded bg-emerald-50">
                <div className="text-[11px] text-emerald-700 uppercase tracking-wide">Buys</div>
                <div className="font-bold text-emerald-800">{filteredSummary.buyCount}</div>
                <div className="text-[11px] text-gray-600">{filteredSummary.buyQty.toLocaleString('en-IN')} qty</div>
                <div className="text-[11px] font-medium text-emerald-700">₹{filteredSummary.buyValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
              </div>
              <div className="p-2 rounded bg-rose-50">
                <div className="text-[11px] text-rose-700 uppercase tracking-wide">Sells</div>
                <div className="font-bold text-rose-800">{filteredSummary.sellCount}</div>
                <div className="text-[11px] text-gray-600">{filteredSummary.sellQty.toLocaleString('en-IN')} qty</div>
                <div className="text-[11px] font-medium text-rose-700">₹{filteredSummary.sellValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
              </div>
              <div className="p-2 rounded bg-blue-50">
                <div className="text-[11px] text-blue-700 uppercase tracking-wide">Net Qty</div>
                <div className={`font-bold ${filteredSummary.netQty >= 0 ? 'text-blue-800' : 'text-rose-800'}`}>
                  {filteredSummary.netQty >= 0 ? '+' : ''}{filteredSummary.netQty.toLocaleString('en-IN')}
                </div>
                <div className="text-[11px] text-gray-500 italic">buy − sell</div>
              </div>
              <div className="p-2 rounded bg-indigo-50">
                <div className="text-[11px] text-indigo-700 uppercase tracking-wide">Net Value</div>
                <div className={`font-bold ${filteredSummary.netValue >= 0 ? 'text-indigo-800' : 'text-rose-800'}`}>
                  {filteredSummary.netValue >= 0 ? '+' : ''}₹{filteredSummary.netValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </div>
                <div className="text-[11px] text-gray-500 italic">cash outflow − inflow</div>
              </div>
              <div className="p-2 rounded bg-gray-50">
                <div className="text-[11px] text-gray-600 uppercase tracking-wide">Members ({filteredSummary.membersCount})</div>
                <div className="text-xs text-gray-800 leading-tight break-words">
                  {filteredSummary.membersList.length ? filteredSummary.membersList.join(', ') : '—'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Transactions Table */}
        <div className="card overflow-hidden mt-4">
          {displayTransactions.length === 0 ? (
            <div className="text-center py-12">
              <FileText size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 mb-4">{hasActiveFilters ? 'No transactions match your filters' : 'No transactions recorded yet'}</p>
              {!hasActiveFilters && (
                <Dialog open={open} onOpenChange={(isOpen) => {
                  setOpen(isOpen);
                  if (!isOpen) {
                    setSymbolQuery('');
                    setSymbolSuggestions([]);
                    setShowSuggestions(false);
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary">
                      <Plus size={18} className="mr-2" />
                      Add Your First Transaction
                    </Button>
                  </DialogTrigger>
                </Dialog>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="w-10"></th>
                    {renderSortHeader('date', 'Date')}
                    {renderSortHeader('type', 'Type')}
                    {renderSortHeader('symbol', 'Symbol')}
                    {renderSortHeader('member', 'Member')}
                    {renderSortHeader('quantity', 'Qty')}
                    {renderSortHeader('price', 'Price')}
                    {renderSortHeader('total', 'Total')}
                    <th className="text-center py-3 px-4 font-semibold text-sm uppercase tracking-wider text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayTransactions.map((txn) => {
                    const isExpanded = expandedId === txn.id;
                    const details = getTransactionDetails(txn);
                    
                    return (
                      <React.Fragment key={txn.id}>
                        <tr 
                          className={`border-b hover:bg-gray-50 cursor-pointer ${isExpanded ? 'bg-blue-50' : ''}`}
                          onClick={() => toggleExpand(txn.id)}
                        >
                          <td className="py-3 px-2 text-center">
                            {isExpanded ? (
                              <ChevronUp size={16} className="text-gray-400" />
                            ) : (
                              <ChevronDown size={16} className="text-gray-400" />
                            )}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap">
                            {new Date(txn.transaction_date).toLocaleDateString('en-IN')}
                          </td>
                          <td className="py-3 px-4">
                            {getTransactionBadge(txn.transaction_type, txn.purchase_type)}
                          </td>
                          <td className="py-3 px-4 font-medium">{txn.symbol}</td>
                          <td className="py-3 px-4">{getMemberName(txn.member_id)}</td>
                          <td className="py-3 px-4 text-right">{txn.quantity.toLocaleString()}</td>
                          <td className="py-3 px-4 text-right">₹{txn.price.toFixed(2)}</td>
                          <td className="py-3 px-4 text-right font-semibold">
                            ₹{(txn.quantity * txn.price).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                          </td>
                          <td className="py-3 px-4">
                            {['buy', 'sell'].includes(txn.transaction_type) ? (
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={(e) => { e.stopPropagation(); setEditTxn(txn); }}
                                  className="p-1.5 rounded hover:bg-blue-100 text-gray-400 hover:text-blue-600"
                                  title="Edit transaction"
                                  data-testid={`edit-txn-${txn.id}`}
                                >
                                  <Pencil size={14} />
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setDeleteTxn(txn); }}
                                  className="p-1.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-600"
                                  title="Delete transaction"
                                  data-testid={`delete-txn-${txn.id}`}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            ) : (
                              <span className="block text-center text-xs text-gray-300">—</span>
                            )}
                          </td>
                        </tr>
                        
                        {isExpanded && (
                          <tr className="bg-blue-50">
                            <td colSpan={9} className="p-0">
                              <div className="px-6 py-4 border-t border-blue-100">
                                <h4 className="text-sm font-semibold text-gray-700 mb-3">Transaction Details</h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                  {details.map((detail, idx) => (
                                    <div key={idx} className="flex items-start gap-2">
                                      <detail.icon size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                                      <div>
                                        <p className="text-xs text-gray-500">{detail.label}</p>
                                        <p className="text-sm font-medium text-gray-700 break-all">{detail.value}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <EditTransactionDialog
          txn={editTxn}
          members={members}
          onClose={() => setEditTxn(null)}
          onSuccess={fetchData}
        />
        <DeleteTransactionDialog
          txn={deleteTxn}
          onClose={() => setDeleteTxn(null)}
          onSuccess={fetchData}
        />
      </div>
    </Layout>
  );
};

export default Transactions;
